import Form, { type IChangeEvent } from '@rjsf/core';
import type { ErrorSchema } from '@rjsf/utils';
import DOMPurify from 'isomorphic-dompurify';
import useStyles from 'isomorphic-style-loader/useStyles';
import jsyaml from 'js-yaml';
import type { JSONSchema7 } from 'json-schema';
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import type { CheckboxProps } from 'semantic-ui-react';
import { Button, Modal, Divider } from 'semantic-ui-react';
import { z, toJSONSchema } from 'zod';

import CancelButton from '@components/cancel-button';
import ErrMessage from '@components/err-message';
import Toggle from '@components/toggle';

import { validator, md } from './constants';
import { isNullableObjectSchema } from './rjsf/nullable-object-field-template';
import { semanticUITheme } from './rjsf/rjsf-semantic-theme';
import rjsfStyles from './rjsf/rjsf-semantic-theme.scss';
import type { JSONFieldEditorProps, JSONFieldEditorPlugin } from './types';
import { cleanFormData } from './utils/data-cleaning-utils';
import {
  generateEmptyStructureFromSchema,
  prepareSchema,
} from './utils/schema';
import { normalizeForValidation, processZodErrors } from './utils/validation';
import s from './json-field-editor.scss';

export const JSONFieldEditor = <TData = unknown,>({
  data,
  example,
  disabled,
  isPartiallyFromEnv,
  inputId,
  onChange,
  zodSchema,
  lookupKey,
  plugins,
  title,
  size = 'small',
  description,
  isDeprecated,
  deprecatedReason,
  renderEditButton,
  showPreview = true,
}: JSONFieldEditorProps<TData>) => {
  useStyles(s, rjsfStyles);

  // State management
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<TData | undefined>(undefined);
  const [validationError, setValidationError] = useState<string[] | null>(null);
  const [errorFieldPaths, setErrorFieldPaths] = useState<Set<string>>(
    new Set(),
  );
  const [displayedErrors, setDisplayedErrors] = useState<ErrorSchema>({});
  const [isNullableEnabled, setIsNullableEnabled] = useState<boolean>(false);

  // Ref for syntax highlighting the YAML preview
  const codePreviewRef = useRef<HTMLElement>(null);

  const formRef = useRef<Form>(null);

  // Get the plugin for the current `lookupKey`
  const currentPlugin = useMemo(
    () => (lookupKey && plugins?.[lookupKey]) || ({} as JSONFieldEditorPlugin),
    [lookupKey, plugins],
  );

  // Generate JSON Schema from Zod schema
  const jsonSchema = useMemo(() => {
    if (!zodSchema) return null;
    try {
      // Note: Using JSONSchema7 type for proper typing
      return toJSONSchema(zodSchema) as JSONSchema7;
    } catch (error) {
      console.error('Failed to convert Zod schema to JSON Schema:', error);
      return null;
    }
  }, [zodSchema]);

  // Check if the root schema is nullable
  const isRootNullable = useMemo(
    () => (jsonSchema ? isNullableObjectSchema(jsonSchema) : false),
    [jsonSchema],
  );

  // Get initial value from `data`, `example`, or `schema`
  const getInitialValue = useCallback((): TData | undefined => {
    const baseValue =
      data ??
      example ??
      (jsonSchema
        ? (generateEmptyStructureFromSchema<TData>(jsonSchema) as TData)
        : ({} as TData));

    // Apply plugin's `initializeData` if present
    if (currentPlugin.initializeData) {
      return currentPlugin.initializeData(data, example || baseValue) as TData;
    }
    return baseValue;
  }, [data, example, jsonSchema, currentPlugin]);

  // Prepare schema for RJSF
  const rjsfSchema = useMemo(
    () => (jsonSchema ? prepareSchema(jsonSchema) : {}),
    [jsonSchema],
  );

  // Create UI schema using plugin or default
  const uiSchema = useMemo(
    () => currentPlugin.createUISchema?.(rjsfSchema, formData) || {},
    [currentPlugin, rjsfSchema, formData],
  );

  // Merge custom fields from plugin with theme fields
  const mergedFields = useMemo(() => {
    if (currentPlugin.customFields) {
      return {
        ...semanticUITheme.fields,
        ...currentPlugin.customFields,
      };
    }
    return semanticUITheme.fields;
  }, [currentPlugin]);

  // Initialize form data when modal opens
  useEffect(() => {
    if (isModalOpen) {
      const initialValue = getInitialValue();
      const hasActualData = data !== null && data !== undefined;

      // For nullable objects, set enabled state based on whether data is `null`
      // or not
      if (isRootNullable) {
        setIsNullableEnabled(hasActualData);
        // Always use `initialValue` which respects the plugin's
        // `initializeData`
        setFormData(initialValue as TData);
      } else {
        setFormData(initialValue as TData);
      }

      // Call plugin's `onModalOpen` if available
      if (currentPlugin.onModalOpen) {
        currentPlugin.onModalOpen();
      }

      // Reset validation state
      setValidationError(null);
      setErrorFieldPaths(new Set());
      setDisplayedErrors({});
    }
  }, [
    isModalOpen,
    data,
    getInitialValue,
    rjsfSchema,
    currentPlugin,
    isRootNullable,
  ]);

  // Reset validation state
  const resetValidation = useCallback(() => {
    setValidationError(null);
    setErrorFieldPaths(new Set());
    setDisplayedErrors({});
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(
    (event?: IChangeEvent) => {
      let submittedData = event?.formData ?? formData;

      // For nullable objects, if disabled, submit `null`
      if (isRootNullable && !isNullableEnabled) {
        onChange(null);
        setIsModalOpen(false);
        resetValidation();
        return;
      }

      // Apply plugin's `transformBeforeSubmit` if available
      if (currentPlugin.transformBeforeSubmit) {
        submittedData = currentPlugin.transformBeforeSubmit(submittedData);
      }

      // Clean the data before submission, and use the plugin's `cleanFormData`
      // if available, otherwise use generic cleaning
      const cleanedData = currentPlugin.cleanFormData
        ? currentPlugin.cleanFormData({
            data: submittedData,
            schema: rjsfSchema,
            fieldPath: [],
          })
        : cleanFormData(submittedData, rjsfSchema, []);

      // Plugin validation
      if (currentPlugin.validateBeforeSubmit) {
        const pluginError = currentPlugin.validateBeforeSubmit(cleanedData);
        if (pluginError) {
          setValidationError([pluginError]);
          return;
        }
      }

      // Validate the cleaned data with Zod. For nullable objects that are
      // enabled but empty (cleanedData is `undefined`), we still want to
      // validate to show appropriate error messages.
      const shouldValidate =
        zodSchema &&
        cleanedData !== null &&
        (cleanedData !== undefined || (isRootNullable && isNullableEnabled));

      if (shouldValidate) {
        try {
          // For enabled nullable objects with undefined `cleanedData`, use an
          // empty object for validation to trigger schema's `superRefine`
          // checks
          const dataToValidate =
            cleanedData !== undefined
              ? cleanedData
              : isRootNullable && isNullableEnabled
                ? {}
                : cleanedData;

          // Normalize undefined string fields to empty strings for better
          // validation error messages (e.g., "Name is required" instead of
          // "Expected string, received undefined")
          const normalizedData = normalizeForValidation(
            dataToValidate,
            rjsfSchema,
          );
          zodSchema.parse(normalizedData);
          resetValidation();
        } catch (error) {
          if (error instanceof z.ZodError) {
            const { errorSchema, errorPaths, errorMessages } =
              processZodErrors(error);
            setErrorFieldPaths(errorPaths);
            setDisplayedErrors(errorSchema);
            setValidationError(errorMessages);
            return; // Don't submit if validation fails
          }
        }
      }

      // Submit the cleaned data
      onChange(cleanedData === undefined ? null : cleanedData);
      setIsModalOpen(false);
      resetValidation();
    },
    [
      formData,
      rjsfSchema,
      onChange,
      resetValidation,
      zodSchema,
      currentPlugin,
      isRootNullable,
      isNullableEnabled,
    ],
  );

  // Handle form cancellation
  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
    setFormData(undefined);
    setIsNullableEnabled(false);
    resetValidation();
  }, [resetValidation]);

  // Handle nullable checkbox toggle
  const handleNullableToggle = useCallback(
    (e: React.FormEvent, checkboxData: CheckboxProps) => {
      e.preventDefault();
      const newEnabled = checkboxData.checked ?? false;
      setIsNullableEnabled(newEnabled);

      if (!newEnabled) {
        // When disabling, keep the form data (it won't be submitted). This
        // allows the user to re-enable without losing their work.
      } else if (formData === null || formData === undefined) {
        // When enabling from `null`, use the initial value which respects
        // plugin initialization
        const initialValue = getInitialValue();
        setFormData(initialValue as TData);
      }

      // Clear validation when toggling
      resetValidation();
    },
    [formData, getInitialValue, resetValidation],
  );

  // Handle form data changes with live validation for error fields
  const handleFormChange = useCallback(
    ({ formData: newData }: IChangeEvent) => {
      setFormData(newData);

      // Re-validate only fields with errors
      if (errorFieldPaths.size > 0 && zodSchema) {
        const cleanedData = currentPlugin.cleanFormData
          ? currentPlugin.cleanFormData({
              data: newData,
              schema: rjsfSchema,
              fieldPath: [],
            })
          : cleanFormData(newData, rjsfSchema, []);

        // For nullable objects that are enabled, validate even if `cleanedData`
        // is `undefined` (empty object case)
        const shouldRevalidate =
          cleanedData !== null &&
          (cleanedData !== undefined || (isRootNullable && isNullableEnabled));

        if (shouldRevalidate) {
          try {
            // For enabled nullable objects with undefined `cleanedData`, use an
            // empty object for validation
            const dataToValidate =
              cleanedData !== undefined
                ? cleanedData
                : isRootNullable && isNullableEnabled
                  ? {}
                  : cleanedData;

            // Normalize undefined string fields to empty strings for better
            // validation error messages during live validation
            const normalizedData = normalizeForValidation(
              dataToValidate,
              rjsfSchema,
            );
            zodSchema.parse(normalizedData);
            // Full validation passed
            resetValidation();
          } catch (error) {
            if (error instanceof z.ZodError) {
              const { errorSchema: newErrorSchema, errorPaths: newErrorPaths } =
                processZodErrors(error, errorFieldPaths);

              // Update displayed errors with only the tracked fields
              setDisplayedErrors(newErrorSchema);
              setErrorFieldPaths(newErrorPaths);

              // Clear validation error if all tracked errors are fixed
              if (newErrorPaths.size === 0) {
                setValidationError(null);
              }
            }
          }
        }
      }
    },
    [
      errorFieldPaths,
      zodSchema,
      rjsfSchema,
      currentPlugin,
      resetValidation,
      isRootNullable,
      isNullableEnabled,
    ],
  );

  // Format the YAML preview
  const yamlPreview = useMemo(() => {
    const value = getInitialValue();
    if (value && typeof value === 'object' && Object.keys(value).length > 0) {
      try {
        return jsyaml.dump(value, {
          indent: 2,
          lineWidth: -1,
          noRefs: true,
          sortKeys: false,
          quotingType: '"',
          forceQuotes: false,
        });
      } catch {
        return JSON.stringify(value, null, 2);
      }
    }
    return null;
  }, [getInitialValue]);

  // Apply syntax highlighting to the YAML preview element
  useEffect(() => {
    if (
      yamlPreview &&
      codePreviewRef.current &&
      typeof window !== 'undefined' &&
      window.Prism
    ) {
      // Use highlightElement to only highlight this specific code block
      setTimeout(() => {
        if (codePreviewRef.current) {
          window.Prism?.highlightElement(codePreviewRef.current);
        }
      }, 0);
    }
  }, [yamlPreview]);

  // Render modal description with Markdown
  const renderDescription = useMemo(() => {
    if (!description && !isDeprecated) return null;

    const content = [
      description,
      isDeprecated &&
        `**Deprecation Warning:** ${
          deprecatedReason ||
          'This configuration option will be removed in a forthcoming release.'
        }`,
    ]
      .filter(Boolean)
      .join('\n\n');

    return md.render(DOMPurify.sanitize(content));
  }, [description, isDeprecated, deprecatedReason]);

  // Format validation errors - display as bulleted list if multiple
  const formattedValidationError = useMemo(() => {
    if (!validationError || validationError.length === 0) return null;

    // Single error - display as plain text
    if (validationError.length === 1) {
      return validationError[0];
    }

    // Multiple errors - display as bulleted list
    // Note: marginTop matches the .message class in err-message.scss
    return (
      <ul className={s.validationErrorList}>
        {validationError.map((error, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={index}>{error}</li>
        ))}
      </ul>
    );
  }, [validationError]);

  return (
    <>
      {/* Main field wrapper */}
      <div className={s.jsonFieldWrapper}>
        {showPreview && yamlPreview && (
          <pre className={s.jsonPreview}>
            <code ref={codePreviewRef} className="language-yaml">
              {yamlPreview}
            </code>
          </pre>
        )}
        {!disabled && !isPartiallyFromEnv ? (
          renderEditButton ? (
            renderEditButton(() => setIsModalOpen(true))
          ) : (
            <Button
              id={inputId}
              primary
              compact
              onClick={() => setIsModalOpen(true)}
              className={s.jsonEditButton}
            >
              Edit
            </Button>
          )
        ) : (
          <div className={s.readOnlyMessage}>
            {isPartiallyFromEnv
              ? 'This configuration is set from multiple sources (environment variables and configuration file) and cannot be edited here'
              : 'This configuration is read-only and cannot be edited'}
          </div>
        )}
      </div>

      {/* Edit modal */}
      <Modal open={isModalOpen} onClose={handleCancel} size={size} closeIcon>
        <Modal.Header>{title || 'Edit JSON Data'}</Modal.Header>
        <Modal.Content scrolling>
          {/* Description and deprecation warning */}
          {renderDescription && (
            <div
              className={s.modalDescription}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: renderDescription,
              }}
            />
          )}
          {/* Validation errors */}
          {formattedValidationError && (
            <ErrMessage
              error
              message={formattedValidationError}
              messageProps={{ size: 'small' }}
            />
          )}
          {/* Nullable object enable/disable checkbox */}
          {isRootNullable && (
            <div className={s.nullableObjectControl}>
              <Toggle
                id="nullable-json-field-toggle"
                label={
                  isNullableEnabled
                    ? 'Click this toggle to revert these values to their default unset state.'
                    : 'These values are currently unset. Click this toggle to provide custom values.'
                }
                checked={isNullableEnabled}
                onChange={handleNullableToggle}
              />
            </div>
          )}
          {/* Plugin preview - only show when enabled or not nullable */}
          {currentPlugin.preview &&
            formData &&
            (!isRootNullable || isNullableEnabled) &&
            currentPlugin.preview(formData)}
          {/* Plugin controls - only show when enabled or not nullable */}
          {currentPlugin.controls &&
            formData &&
            (!isRootNullable || isNullableEnabled) &&
            currentPlugin.controls(formData, setFormData)}
          {/* Divider after nullable toggle and/or preview/controls */}
          {((isRootNullable && isNullableEnabled) ||
            (currentPlugin.preview &&
              formData &&
              (!isRootNullable || isNullableEnabled)) ||
            (currentPlugin.controls &&
              formData &&
              (!isRootNullable || isNullableEnabled))) && <Divider />}
          {/* RJSF form - show only if enabled or not nullable */}
          {isModalOpen &&
            formData !== null &&
            (!isRootNullable || isNullableEnabled) && (
              <Form
                ref={formRef}
                schema={rjsfSchema}
                uiSchema={uiSchema}
                validator={validator}
                formData={formData}
                onChange={handleFormChange}
                onSubmit={handleSubmit}
                onError={() => {}}
                showErrorList={false}
                liveValidate={false}
                noHtml5Validate
                widgets={semanticUITheme.widgets}
                fields={mergedFields}
                templates={semanticUITheme.templates}
                extraErrors={displayedErrors}
              >
                {/* Hidden submit button to enable Enter key submission */}
                <button
                  type="submit"
                  className={s.hiddenFormButtons}
                  aria-label="Submit form"
                />
              </Form>
            )}
        </Modal.Content>
        <Modal.Actions>
          <CancelButton onClick={handleCancel} />
          <Button primary onClick={() => handleSubmit()}>
            Apply
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default JSONFieldEditor;
